import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import { FC, MutableRefObject } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import searchIcon from '../../assets/search-icon.svg'
import clearIcon from '../../assets/search-clear-icon.svg'
import { sansSerifFontFamily } from 'utils/styles'

const HEIGHT = 48
const BORDER_RADIUS = 14

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
  },
  fieldContainer: {
    flexGrow: 1,
    height: HEIGHT,
    width: '100%',
    display: 'flex',
  },
  input: {
    width: '100%',
    height: '100%',
    color: '#FFFFFF',
    background: 'rgba(100, 100, 100, 0.6);',
    border: 'none',
    borderRadius: BORDER_RADIUS,
    paddingLeft: 50,
    paddingRight: HEIGHT,
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 500,

    '&:focus': {
      outline: 'none',
    },

    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.8);',
      opacity: 0.8,
    },
  },
  iconContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    paddingLeft: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  clearButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    paddingRight: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
  },
})

interface Props {
  value?: string
  onChange?: (value: string) => void
  onClear?: () => void
  onFocus?: () => void
  className?: string
  inputRef?: MutableRefObject<HTMLInputElement | null>
}

export const SearchField: FC<Props> = flowMax(
  addDisplayName('SearchField'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event.target.value)
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ value, onChange, onClear, onFocus, inputRef, classes, className, t }) => (
    <div className={cx(classes.container, className)}>
      <div className={classes.fieldContainer}>
        <input
          type="search"
          placeholder={t('search.placeholder')}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          ref={inputRef}
          className={classes.input}
        />
      </div>
      <div className={classes.iconContainer}>
        <img src={searchIcon} alt="" />
      </div>
      {onClear && value && value.length > 0 && (
        <button onClick={onClear} className={classes.clearButton}>
          <img src={clearIcon} alt={t('search.clear')} />
        </button>
      )}
    </div>
  )
)
